import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import {
    Field,
    Form,
    FormElement,
    FormFieldSet,
    type FormRenderProps
} from "@progress/kendo-react-form";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { arrowRightIcon } from "@progress/kendo-svg-icons";
import { useResetPasswordConfirmMutation } from "../../api/userApi";
import { emailValidator, FormInputField } from "../../components/kendo-form-controls";
import { Loader } from "@progress/kendo-react-indicators";

type ResetPasswordFormValues = {
    code: string;
    email: string;
    password: string;
    confirmPassword: string;
};

const passwordValidator = (value: string | undefined): string | undefined => {
    if (!value) return "Password is required.";

    return value.length >= 8
        ? undefined
        : "Password must be at least 8 characters.";
};

const confirmPasswordValidator = (password: string | undefined) =>
    (value: string | undefined): string | undefined => {
        if (!value) return "Confirm password is required.";

        return value === password
            ? undefined
            : "Passwords do not match.";
    };

const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [resetPasswordConfirm, { isLoading }] = useResetPasswordConfirmMutation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const initialValues = useMemo<ResetPasswordFormValues>(() => ({
        code: searchParams.get("code") ?? "",
        email: searchParams.get("email") ?? "",
        password: "",
        confirmPassword: ""
    }), [searchParams]);

    const handleSubmitAsync = async (values: ResetPasswordFormValues): Promise<void> => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!values.email || !values.code) {
            setErrorMessage("This password reset link is missing required information. Please request a new reset link.");
            return;
        }

        try {
            const response = await resetPasswordConfirm({
                email: values.email,
                code: values.code,
                password: values.password,
                confirm_password: values.confirmPassword
            }).unwrap();

            if (!response.is_success) {
                setErrorMessage(response.error_message || "Unable to reset your password. Please request a new reset link and try again.");
                return;
            }

            setSuccessMessage("Your password has been reset successfully.");
        } catch {
            setErrorMessage("Unable to reset your password. Please check your details and try again.");
        }
    };

    return (
        <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-body-tertiary px-3 py-4">
            <Card
                className="w-100 rounded-4 border border-secondary-subtle shadow-sm"
                style={{ maxWidth: "440px" }}
            >
                <CardBody className="px-4 py-5 p-sm-5">
                    <div className="mb-4">
                        <h1 className="h3 fw-bold mb-2 text-body">Reset password</h1>
                        <p className="mb-0 text-body-secondary">
                            Enter your email and choose a new password.
                        </p>
                    </div>

                    {errorMessage && (
                        <div className="alert alert-danger small mb-3" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {successMessage && (
                        <div className="alert alert-success small mb-3" role="alert">
                            {successMessage}
                        </div>
                    )}

                    {isLoading && (
                        <div className="text-center">
                            <Loader type="pulsing" size="large" />
                        </div>
                    )}

                    {!successMessage && !isLoading && (
                        <Form
                            initialValues={initialValues}
                            onSubmit={(values: Record<string, unknown>) => void handleSubmitAsync(values as ResetPasswordFormValues)}
                            render={(formRenderProps: FormRenderProps) => (
                                <FormElement>
                                    <Field
                                        name="code"
                                        component="input"
                                        type="hidden"
                                    />

                                    <FormFieldSet className="mb-0">
                                        <Field
                                            id="reset-email"
                                            name="email"
                                            label="Work Email"
                                            type="email"
                                            component={FormInputField}
                                            validator={emailValidator}
                                        />

                                        <Field
                                            id="reset-password"
                                            name="password"
                                            label="New Password"
                                            type="password"
                                            component={FormInputField}
                                            validator={passwordValidator}
                                        />

                                        <Field
                                            id="reset-confirm-password"
                                            name="confirmPassword"
                                            label="Confirm Password"
                                            type="password"
                                            component={FormInputField}
                                            validator={confirmPasswordValidator(formRenderProps.valueGetter("password") as string | undefined)}
                                        />
                                    </FormFieldSet>

                                    <Button
                                        type="submit"
                                        className="w-100 border-0 rounded-3 fw-semibold py-2 mt-3 shadow-sm"
                                        disabled={!formRenderProps.allowSubmit || isLoading}
                                        style={{ backgroundColor: "#2f8378", color: "#ffffff" }}
                                    >
                                        <span className="d-inline-flex align-items-center justify-content-center gap-2">
                                            {isLoading ? "Resetting password" : "Reset password"}
                                            <SvgIcon icon={arrowRightIcon} style={{ width: "16px", height: "16px" }} />
                                        </span>
                                    </Button>

                                    <p className="mb-0 mt-4 text-center text-body-secondary small">
                                        Remember your password?&nbsp;
                                        <Link className="link-success fw-semibold link-underline-opacity-0" to="/login">
                                            Back to login
                                        </Link>
                                    </p>
                                </FormElement>
                            )}
                        />
                    )}

                    {successMessage && (
                        <div className="d-grid gap-3">
                            <Link
                                className="btn border-0 rounded-3 fw-semibold py-2 shadow-sm"
                                style={{ backgroundColor: "#2f8378", color: "#ffffff" }}
                                to="/login"
                            >
                                Go to login page
                            </Link>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default ResetPassword;
